"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { TableCell, TableRow } from "@/components/ui/table";
import { Building2, User, AlertCircle } from "lucide-react";
import { formatNumber } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { toast } from "sonner";
import {
  STATUS_LABELS,
  statusBadgeClass,
  type MockInvoice,
} from "./types";

interface InvoiceRowProps {
  invoice: MockInvoice;
  venueId: number;
  // The note column is only useful in the pending_manual section, where
  // admins drop context for invoices they need to handle manually. The
  // auto-send and completed sections hide it.
  showNote: boolean;
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const minutes = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3600_000);
  const days = Math.round(abs / 86400_000);

  let core: string;
  if (minutes < 60) core = `${minutes} min`;
  else if (hours < 24) core = `${hours} h`;
  else core = `${days} d`;

  return future ? `in ${core}` : `${core} ago`;
}

export function InvoiceRow({ invoice, venueId, showNote }: InvoiceRowProps) {
  const [draft, setDraft] = useState("");

  // The Invoices page is rendered in both the super-admin and booking
  // portals. Detect which one we're in from the current path so the
  // booking-number link stays inside the same portal — otherwise a super
  // admin clicking through gets bounced into the booking-staff portal
  // (which has a different nav and lacks the Partners menu, etc.).
  const pathname = usePathname();
  const portalBase = pathname?.startsWith("/superadmin/")
    ? "superadmin"
    : "booking";
  const [saving, setSaving] = useState(false);

  // Persist on blur as a new booking_notes row with category='manual_invoice'.
  // Each save creates a fresh row — the column is an "add a note" affordance,
  // not an editor for an existing note (those live in the booking drawer).
  const handleBlur = async () => {
    const trimmed = draft.trim();
    if (!trimmed || saving) return;

    setSaving(true);
    try {
      const res = await fetch(`/api/bookings/${invoice.booking_id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          note_content: trimmed,
          category: "manual_invoice",
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to save note");
      }
      setDraft("");
      toast.success("Note added");
    } catch (err) {
      toast.error((err as Error).message || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow className="hover:bg-gray-50 align-top">
      <TableCell className="px-6 py-4">
        <Link
          href={`/${portalBase}/venue/${venueId}/bookings/${invoice.booking_id}`}
          className="font-medium text-purple-700 hover:text-purple-900 hover:underline"
        >
          {invoice.booking_no}
        </Link>
        {invoice.invoice_number && (
          <div className="text-xs text-gray-500 mt-1">
            {invoice.invoice_number}
          </div>
        )}
      </TableCell>

      <TableCell className="px-6 py-4">
        <div className="flex items-start gap-2">
          {invoice.is_partner ? (
            <Building2 className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
          ) : (
            <User className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {invoice.customer_name}
            </div>
            {invoice.is_partner && invoice.end_customer_name && (
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                End customer: {invoice.end_customer_name}
              </div>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell className="px-6 py-4 whitespace-nowrap text-right">
        <div className="font-semibold text-gray-900">
          {getCurrencySymbol(invoice.currency)} {formatNumber(invoice.amount)}
        </div>
        {Number.isFinite(invoice.amount_excl_vat) && (
          <div className="text-xs text-gray-500 mt-0.5">
            excl. VAT {getCurrencySymbol(invoice.currency)}{" "}
            {formatNumber(invoice.amount_excl_vat)}
          </div>
        )}
      </TableCell>

      <TableCell className="px-6 py-4">
        <Badge
          variant="secondary"
          title={invoice.flag_reason || undefined}
          className={`${statusBadgeClass(invoice.status)} ${
            invoice.flag_reason ? "cursor-help" : ""
          }`}
        >
          <span className="flex items-center gap-1">
            {STATUS_LABELS[invoice.status]}
            {invoice.flag_reason && (
              <AlertCircle className="h-3 w-3 opacity-70" />
            )}
          </span>
        </Badge>
        {invoice.scheduled_send_at && (
          <div className="text-xs text-gray-500 mt-1">
            Sends {formatRelative(invoice.scheduled_send_at)}
          </div>
        )}
      </TableCell>

      <TableCell className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-700">
          {formatRelative(invoice.last_action_at)}
        </div>
        {invoice.last_action_by && (
          <div className="text-xs text-gray-500 mt-1">
            {invoice.last_action_by}
          </div>
        )}
      </TableCell>

      {/* Admin / booking-staff can drop a manual_invoice note here. Each
          save creates a new booking_notes row (category='manual_invoice'),
          joining the same feed bar staff write to from the drawer. Only
          rendered in the pending_manual tab — auto-send and completed
          rows have no use for it. */}
      {showNote && (
        <TableCell className="px-6 py-4 min-w-[220px]">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            placeholder="Add a note…"
            rows={2}
            disabled={saving}
            className="text-sm resize-none"
          />
        </TableCell>
      )}
    </TableRow>
  );
}
